import type { Invitation } from '../../lib/api.js';
import { Icon } from '../Icon.js';
import { MoreMenu, type MenuItem } from '../ui/MoreMenu.js';
import { StatusPill } from '../ui/StatusPill.js';
import { INVITATION_PILL, formatDay, invitationState, roleLabel, type InvitationState } from './roles.js';

/** What the person may do to invitations; each refusal is the sentence to show, null when allowed. */
export interface InvitationRights {
  /** Why a new link cannot be sent: it revokes the old one AND invites again. */
  readonly renewRefusal: string | null;
  readonly revokeRefusal: string | null;
  /** Why "Invite again" cannot be used. */
  readonly inviteRefusal: string | null;
}

export interface InvitationActions {
  readonly onRenew: (invitation: Invitation) => void;
  readonly onRevoke: (invitation: Invitation) => void;
  readonly onInviteAgain: (invitation: Invitation) => void;
}

function whenLine(invitation: Invitation, state: InvitationState): string | null {
  const day = formatDay(invitation.expiresAt);
  if (day === '') return null;
  if (state === 'PENDING') return `Link valid until ${day}`;
  if (state === 'EXPIRED') return `Link expired on ${day}`;
  return null;
}

/**
 * ONE INVITATION: the address, the job it was sent for, where it stands, and
 * the one thing to do about it.
 *
 * There is no "resend" on the server. A new link is the honest equivalent —
 * revoke this invitation, then invite the same address again for the same job —
 * and the row calls it that, because the link in the earlier email stops
 * working. An expired invitation needs exactly this: it can no longer be
 * accepted, and while it stays pending it blocks a fresh invitation to the same
 * address.
 */
export function InvitationRow({
  invitation,
  now,
  rights,
  actions,
  compact = false,
}: {
  invitation: Invitation;
  now: number;
  rights: InvitationRights;
  actions: InvitationActions;
  compact?: boolean;
}) {
  const state = invitationState(invitation, now);
  const pill = state === 'OTHER' ? null : INVITATION_PILL[state];
  const when = whenLine(invitation, state);
  const open = state === 'PENDING' || state === 'EXPIRED';

  const menu: MenuItem[] = [];
  if (open) {
    menu.push({
      id: 'revoke',
      label: 'Revoke invitation',
      tone: 'danger',
      testId: `revoke-${invitation.id}`,
      disabled: rights.revokeRefusal !== null,
      ...(rights.revokeRefusal === null ? {} : { hint: rights.revokeRefusal }),
      onSelect: () => {
        actions.onRevoke(invitation);
      },
    });
  }
  if (state === 'REVOKED') {
    menu.push({
      id: 'invite-again',
      label: 'Invite again',
      testId: `reinvite-${invitation.id}`,
      disabled: rights.inviteRefusal !== null,
      ...(rights.inviteRefusal === null ? {} : { hint: rights.inviteRefusal }),
      onSelect: () => {
        actions.onInviteAgain(invitation);
      },
    });
  }

  return (
    <li
      className="team-invite"
      data-compact={compact ? 'true' : undefined}
      data-state={state}
      data-testid={`invitation-${invitation.id}`}
    >
      <span className="team-invite-icon" aria-hidden="true">
        <Icon name="mail" size={18} />
      </span>
      <div className="team-invite-main">
        <div className="team-invite-line">
          <span className="team-invite-email" data-testid={`invitation-email-${invitation.id}`}>
            {invitation.email}
          </span>
          {pill === null ? (
            <span className="team-invite-other" data-testid={`invitation-status-${invitation.id}`}>
              {invitation.status.toLowerCase()}
            </span>
          ) : (
            <StatusPill state={pill.state} label={pill.label} size="sm" testId={`invitation-status-${invitation.id}`} />
          )}
        </div>
        <span className="team-invite-meta" data-testid={`invitation-role-${invitation.id}`}>
          {roleLabel(invitation.authorizationRole)}
          {invitation.productionTitle ? ` · ${invitation.productionTitle}` : ''}
        </span>
        {when === null ? null : (
          <span className="team-invite-when" data-testid={`invitation-when-${invitation.id}`}>
            {when}
          </span>
        )}
      </div>
      {open || menu.length > 0 ? (
        <div className="team-invite-actions">
          {open ? (
            <button
              type="button"
              className="ctl"
              data-testid={`renew-${invitation.id}`}
              disabled={rights.renewRefusal !== null}
              title={rights.renewRefusal ?? 'Revokes this invitation and emails a new link'}
              onClick={() => {
                actions.onRenew(invitation);
              }}
            >
              Send a new link
            </button>
          ) : null}
          {menu.length > 0 ? (
            <MoreMenu testId={`invitation-menu-${invitation.id}`} label={`Actions for ${invitation.email}`} items={menu} />
          ) : null}
        </div>
      ) : null}
    </li>
  );
}
